/**
 * Tooltip Component
 * 
 * A web component that displays a tooltip using the native Popover API
 */

if (!customElements.get('tooltip-component')) {
  customElements.define(
    'tooltip-component',
    class TooltipComponent extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        // Prevent double initialization
        if (this.shadowRoot || this.hasAttribute('data-initialized')) {
          return;
        }

        this.setAttribute('data-initialized', 'true');
        
        // Get attributes
        const triggerText = this.getAttribute('trigger-text') || 'i';
        const title = this.getAttribute('title') || '';

        // Get slot content
        const slotContent = this.querySelector('[slot="content"]');
        
        if (!slotContent && !this.getAttribute('content')) {
          console.warn('TooltipComponent: No content provided');
          return;
        }

        // Generate unique ID
        const tooltipId = `tooltip-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

        // Create trigger button
        const trigger = document.createElement('button');
        trigger.type = 'button';
        trigger.className = 'tooltip__trigger';
        trigger.textContent = triggerText;
        trigger.setAttribute('popovertarget', tooltipId);
        trigger.setAttribute('aria-label', 'Show tooltip');

        // Create popover element
        const popover = document.createElement('div');
        popover.id = tooltipId;
        popover.className = 'tooltip__popover';
        popover.popover = 'auto'; // Use property, not attribute

        // Create content wrapper
        const contentWrapper = document.createElement('div');
        contentWrapper.className = 'tooltip__content-wrapper';

        // Add title if provided
        if (title) {
          const titleEl = document.createElement('div');
          titleEl.className = 'tooltip__title';
          titleEl.textContent = title;
          contentWrapper.appendChild(titleEl);
        }

        // Add content
        if (slotContent) {
          const clonedContent = slotContent.cloneNode(true);
          clonedContent.removeAttribute('slot');
          contentWrapper.appendChild(clonedContent);
        } else if (this.getAttribute('content')) {
          const contentEl = document.createElement('div');
          contentEl.className = 'tooltip__content';
          contentEl.innerHTML = this.getAttribute('content');
          contentWrapper.appendChild(contentEl);
        }

        popover.appendChild(contentWrapper);

        // Clear and rebuild
        this.innerHTML = '';
        this.appendChild(trigger);
        this.appendChild(popover);

        // Store references
        this._trigger = trigger;
        this._popover = popover;
        this._isHoverDevice = window.matchMedia('(hover: hover)').matches;

        // Add data attribute for CSS targeting
        if (this._isHoverDevice) {
          this.classList.add('tooltip--hover-device');
        } else {
          this.classList.add('tooltip--touch-device');
        }

        // For hover devices: show on hover, hide on mouse leave
        if (this._isHoverDevice) {
          const showTooltip = () => {
            this.positionPopoverAbove();
          };

          trigger.addEventListener('mouseenter', showTooltip);

          trigger.addEventListener('mouseleave', () => {
            popover.hidePopover();
          });

          // Also handle hover on the popover itself
          popover.addEventListener('mouseenter', () => {
            popover.showPopover();
          });

          popover.addEventListener('mouseleave', () => {
            popover.hidePopover();
          });

          // Reposition on window resize
          window.addEventListener('resize', () => {
            if (popover.matches(':popover-open')) {
              this.positionPopoverAbove();
            }
          });
        }

        // For touch devices: show centered on click
        trigger.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          
          if (popover.matches(':popover-open')) {
            popover.hidePopover();
          } else {
            if (!this._isHoverDevice) {
              // Position first, then show
              this.centerPopover();
            } else {
              // For hover devices, just toggle (positioning handled by hover)
              popover.showPopover();
            }
          }
        });

        // Close on backdrop click
        popover.addEventListener('click', (e) => {
          if (e.target === popover) {
            popover.hidePopover();
          }
        });
      }

      positionPopoverAbove() {
        if (!this._trigger || !this._popover) return;

        const triggerRect = this._trigger.getBoundingClientRect();
        const wasOpen = this._popover.matches(':popover-open');
        
        // Show popover first to get its dimensions
        if (!wasOpen) {
          this._popover.showPopover();
        }
        
        // Wait for next frame to ensure dimensions are calculated
        requestAnimationFrame(() => {
          const popoverRect = this._popover.getBoundingClientRect();
          
          // Position above the trigger, centered horizontally
          const gap = 8; // 8px gap between trigger and popover
          const top = triggerRect.top - popoverRect.height - gap;
          const left = triggerRect.left + (triggerRect.width / 2) - (popoverRect.width / 2);
          
          // Ensure it doesn't go off screen
          const finalLeft = Math.max(8, Math.min(left, window.innerWidth - popoverRect.width - 8));
          const finalTop = Math.max(8, top);
          
          this._popover.style.top = `${finalTop}px`;
          this._popover.style.left = `${finalLeft}px`;
          this._popover.style.right = 'auto';
          this._popover.style.bottom = 'auto';
          this._popover.style.transform = 'none';
          this._popover.style.margin = '0';
        });
      }

      centerPopover() {
        if (!this._popover) return;

        // Temporarily show to get dimensions, then position
        const wasOpen = this._popover.matches(':popover-open');
        if (!wasOpen) {
          this._popover.showPopover();
        }

        requestAnimationFrame(() => {
          const popoverRect = this._popover.getBoundingClientRect();
          const centerX = (window.innerWidth - popoverRect.width) / 2;
          const centerY = (window.innerHeight - popoverRect.height) / 2;
          
          this._popover.style.top = `${centerY}px`;
          this._popover.style.left = `${centerX}px`;
          this._popover.style.right = 'auto';
          this._popover.style.bottom = 'auto';
          this._popover.style.transform = 'none';
          this._popover.style.margin = '0';
        });
      }

      disconnectedCallback() {
        // Cleanup if needed
        if (this._popover && this._popover.matches(':popover-open')) {
          this._popover.hidePopover();
        }
      }
    }
  );
}
